import { type ComponentProps, type ComponentType, Fragment, type ReactNode, useEffect, useMemo, useRef } from 'react';
import logError from '@alltrails/core/utils/logError';
import {
  type PageUI,
  type PageUICommunityInsightSection,
  type PageUISection,
  type PageUISectionType,
  sectionKeys
} from '../../../utils/PageUI/pageUI';
import PageUICommunityInsight from '../PageUICommunityInsight/PageUICommunityInsight';
import PageUIDivider from '../PageUIDivider/PageUIDivider';
import PageUISummary from '../PageUISummary/PageUISummary';
import PageUITextBlock from '../PageUITextBlock/PageUITextBlock';
import PageUITrailResult from '../PageUITrailResult/PageUITrailResult';

type SectionProps = {
  'llm-summary': ComponentProps<typeof PageUISummary>;
  'llm-error-summary': ComponentProps<typeof PageUISummary>;
  'trail-result': ComponentProps<typeof PageUITrailResult>;
  'community-insight': ComponentProps<typeof PageUICommunityInsight>;
  divider: Record<never, never>;
  'text-block': ComponentProps<typeof PageUITextBlock>;
};

/**
 * Props for a consumer's component for section type `T`: the built-in component's props, plus that component as
 * `Fallback` so a restyle can wrap or reuse it.
 */
export type PageUISectionComponentProps<T extends PageUISectionType> = SectionProps[T] & {
  Fallback: ComponentType<SectionProps[T]>;
};

/** Consumer components by section type. Types left out render their built-in component. */
export type PageUISectionComponents = { [T in PageUISectionType]?: ComponentType<PageUISectionComponentProps<T>> };

const renderComponent = <P extends object>(
  Fallback: ComponentType<P>,
  Component: ComponentType<P & { Fallback: ComponentType<P> }> | undefined,
  props: P
) => (Component ? <Component {...props} Fallback={Fallback} /> : <Fallback {...props} />);

/** The id of the trail a section is a community insight about, if it is one. */
const insightTrailId = (section: PageUISection) =>
  section.sectionType === 'community-insight' && section.recordType === 'trail' ? section.recordId : undefined;

/**
 * Groups the page's community insights by the trail result they belong to. An insight without a trail result on the page
 * isn't included, so it still renders on its own.
 */
const insightsByTrailResult = (sections: PageUISection[]) => {
  const trailIds = new Set(sections.flatMap(section => (section.sectionType === 'trail-result' ? [section.trailId] : [])));
  const insights = new Map<number, PageUICommunityInsightSection[]>();

  sections.forEach(section => {
    const trailId = insightTrailId(section);

    if (section.sectionType === 'community-insight' && trailId !== undefined && trailIds.has(trailId)) {
      insights.set(trailId, [...(insights.get(trailId) ?? []), section]);
    }
  });

  return insights;
};

// Each known section type renders the consumer's component for it & falls back to the default if nothing is provided.
// Adding a section type means adding it to SectionProps and a case here.
const renderSection = (
  section: PageUISection,
  components: PageUISectionComponents = {},
  trailInsights?: Map<number, PageUICommunityInsightSection[]>
): ReactNode => {
  switch (section.sectionType) {
    case 'llm-summary':
    case 'llm-error-summary':
      return renderComponent(PageUISummary, components[section.sectionType], { summary: section.summary });
    case 'trail-result':
      return renderComponent(PageUITrailResult, components[section.sectionType], { insights: trailInsights?.get(section.trailId), section });
    case 'community-insight':
      return renderComponent(PageUICommunityInsight, components[section.sectionType], { section });
    case 'divider':
      return renderComponent(PageUIDivider, components[section.sectionType], {});
    case 'text-block':
      return renderComponent(PageUITextBlock, components[section.sectionType], { body: section.body });
    default:
      // An unknown/un-supported section was added to the server contract, render nothing & log the unknonwn section type
      return null;
  }
};

const logUnknownSection = (sectionType: string) => {
  logError(new Error(`Unknown SDUI section type: ${sectionType}`));
};

type Props = {
  /**
   * Restyles section types for this surface. Each component gets the built-in component's props plus `Fallback`.
   * Pass a stable object (a module-level constant or `useMemo`), since a new object re-renders every section.
   */
  components?: PageUISectionComponents;
  /**
   * Passes each community insight to the trail result it belongs to (`recordType` `trail` and its `recordId`), which quotes
   * it under its card, instead of rendering it on its own. Insights without a trail result on the page still render on their own.
   */
  insightsInTrailResults?: boolean;
  page: PageUI;
  /** Called once per unknown section type per page. Defaults to logging the error. */
  onUnknownSection?: (sectionType: string) => void;
};

/**
 * Renders a page's sections in the order the server sent them. The list owns the order, the keys, unknown section types
 * (unknowns render nothing) and, with `insightsInTrailResults`, which insights move into their trail result; consumers can
 * restyle any known type through `components`, falling back to the built-in component for the rest.
 */
export default function PageUISectionList({ components, insightsInTrailResults, page, onUnknownSection = logUnknownSection }: Props) {
  const reported = useRef<{ page: PageUI; sectionTypes: Set<string> }>(undefined);

  const { nodes, unknownSectionTypes } = useMemo(() => {
    const unknownSectionTypes = new Set<string>();
    const nodes: ReactNode[] = [];
    const keys = sectionKeys(page.sections);
    const trailInsights = insightsInTrailResults ? insightsByTrailResult(page.sections) : undefined;

    page.sections.forEach((section, index) => {
      const trailId = insightTrailId(section);

      // Rendered inside its trail result instead
      if (trailId !== undefined && trailInsights?.has(trailId)) {
        return;
      }

      const node = renderSection(section, components, trailInsights);

      if (node) {
        nodes.push(<Fragment key={keys[index]}>{node}</Fragment>);
      } else {
        unknownSectionTypes.add(section.sectionType);
      }
    });

    return { nodes, unknownSectionTypes };
  }, [components, insightsInTrailResults, page]);

  useEffect(() => {
    if (reported.current?.page !== page) {
      reported.current = { page, sectionTypes: new Set() };
    }

    const { sectionTypes } = reported.current;

    unknownSectionTypes.forEach(sectionType => {
      if (!sectionTypes.has(sectionType)) {
        sectionTypes.add(sectionType);
        onUnknownSection(sectionType);
      }
    });
  }, [onUnknownSection, page, unknownSectionTypes]);

  return <>{nodes}</>;
}
